import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LeadListItem } from "@alm/api-contracts";
import { GitMerge, Plus } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { TableCard } from "@/components/app/patterns/table-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import {
  listParam,
  optionalInt,
  optionalOneOf,
  optionalString,
  stringParams,
  KEEP_PLACE,
  optionalSearch,
} from "@/lib/search";
import { useCan } from "@/lib/policy";
import { LeadBoard } from "./-components/lead-board";
import { LeadViewToggle } from "./-components/lead-view-toggle";
import { LeadsEmpty } from "./-components/leads-empty";
import { saveView, storedView } from "./-lib/lead-view";
import { type CreatedLead, LeadFormDialog } from "./-components/lead-form-dialog";
import { LeadWelcomeOverlay } from "./-components/lead-welcome-overlay";
import { leadListInput, type Status } from "./-components/lead-list-input";
import { isLeadClosed } from "./-components/lead-status";
import { LeadLostDialog } from "./-components/lead-lost-dialog";
import { LeadMergeDialog } from "./-components/lead-merge-dialog";
import { useEveryLead } from "./-components/use-every-lead";
import { LeadsTable } from "./-components/leads-table";
import { type Sort } from "@/routes/_authenticated/_crm/crm/leads/-components/leads-table-types";
import { EMPTY_FILTERS, LeadsToolbar, type LeadFilters } from "./-components/leads-toolbar";

type LeadsSearch = {
  edit?: string;
  q?: string;
  status?: string;
  scope?: "all" | "active";
  assignee?: string;
  source?: string;
  tier?: string;
  from?: string;
  to?: string;
  tags?: string;
  sort?: string;
  dir?: "desc";
  page?: number;
  size?: number;
  view?: "list" | "kanban";
};

export const Route = createFileRoute("/_authenticated/_crm/crm/leads/")({
  validateSearch: (search: Record<string, unknown>): LeadsSearch => ({
    edit: optionalString(search.edit),
    q: optionalSearch(search.q),
    ...stringParams(["status", "assignee", "source", "tier", "from", "to", "tags", "sort"], search),
    scope: optionalOneOf(["all", "active"] as const, search.scope),
    dir: optionalOneOf(["desc"] as const, search.dir),
    page: optionalInt(search.page, 2, 100000),
    size: optionalInt(search.size, 5, 200),
    view: optionalOneOf(["list", "kanban"] as const, search.view),
  }),
  component: LeadsPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

function LeadsPage() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const { edit } = params;
  const navigate = useNavigate();
  const go = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<LeadsSearch>) =>
    void go({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const queryClient = useQueryClient();
  const canWrite = useCan("crm:manage");
  const view = params.view ?? storedView();
  const setView = (next: "list" | "kanban") => {
    saveView(next);
    patch({ view: next === "list" ? undefined : next });
  };
  const { text: searchText, change: setSearchText } = useSearchParamText(params.q, (q) =>
    patch({ q, page: undefined }),
  );
  const statuses = listParam(params.status);
  // A link straight to lost or converted leads must not land on a scope that hides them.
  const defaultScope = statuses.some((status) => isLeadClosed(status as Status)) ? "all" : "active";
  const filters: LeadFilters = {
    scope: params.scope ?? defaultScope,
    search: searchText,
    statuses,
    assignedToId: params.assignee ?? "all",
    referralSourceId: params.source ?? "all",
    tier: params.tier ?? "all",
    createdFrom: params.from ?? "",
    createdTo: params.to ?? "",
    tagIds: listParam(params.tags),
  };
  const sort: Sort = { key: params.sort ?? "", direction: params.dir ?? "asc" };
  const page = params.page ?? 1;
  const pageSize = params.size ?? 25;
  const [editing, setEditing] = useState<{ id: string } | "new" | null>(() =>
    edit === "new" ? "new" : edit ? { id: edit } : null,
  );
  const [deleting, setDeleting] = useState<LeadListItem | null>(null);
  const [merging, setMerging] = useState(false);
  const [losing, setLosing] = useState<LeadListItem | null>(null);
  const [welcoming, setWelcoming] = useState<CreatedLead | null>(null);
  const search = params.q ?? "";

  const board = view === "kanban";
  const input = leadListInput(facilityId, filters, sort, search);
  const listPage = useQuery({
    ...orpc.crm.leads.list.queryOptions({
      input: { ...input, limit: pageSize, offset: (page - 1) * pageSize },
    }),
    enabled: !board,
  });
  // The board shows every lead the filters match.
  const everyLead = useEveryLead(input, board);
  const leads = board ? everyLead : listPage;
  // Merge offers every lead in the building, not the page on screen.
  const mergeable = useEveryLead({ facilityId, sort: "name", dir: "asc" }, merging);
  const editingLead = useQuery({
    ...orpc.crm.leads.get.queryOptions({
      input: { leadId: editing && editing !== "new" ? editing.id : "" },
    }),
    enabled: Boolean(editing && editing !== "new"),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.crm.leads.key() });

  const setStatus = useMutation(
    orpc.crm.leads.setStatus.mutationOptions({
      onSuccess: async () => {
        setLosing(null);
        await refresh();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.crm.leads.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Lead deleted");
        await refresh();
      },
      onError: fail,
    }),
  );

  const changeFilters = (next: LeadFilters) => {
    if (next.search !== filters.search) setSearchText(next.search);
    patch({
      status: next.statuses.join(",") || undefined,
      scope: next.scope === defaultScope ? undefined : next.scope,
      assignee: next.assignedToId === "all" ? undefined : next.assignedToId,
      source: next.referralSourceId === "all" ? undefined : next.referralSourceId,
      tier: next.tier === "all" ? undefined : next.tier,
      from: next.createdFrom || undefined,
      to: next.createdTo || undefined,
      tags: next.tagIds.join(",") || undefined,
      page: undefined,
    });
  };
  const open = (lead: LeadListItem) =>
    navigate({ to: "/crm/leads/$leadId", params: { leadId: lead.id } });
  const total = leads.data?.total ?? 0;
  const rows = leads.data?.rows ?? [];
  const closeEditor = () => {
    setEditing(null);
    if (edit) patch({ edit: undefined });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Leads"
        count={leads.isPending || leads.isError ? undefined : total}
        actions={
          <>
            <LeadViewToggle view={view} onChange={setView} />
            {canWrite ? (
              <>
                <Button variant="ghost" onClick={() => setMerging(true)}>
                  <GitMerge className="size-4" />
                  Merge
                </Button>
                <Button onClick={() => setEditing("new")}>
                  <Plus className="size-4" />
                  Add lead
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <LeadsToolbar facilityId={facilityId} filters={filters} onChange={changeFilters} />

      {board ? (
        leads.isPending ? (
          <Skeleton className="h-96 w-full" />
        ) : (
          <LeadBoard
            rows={rows}
            facilityId={facilityId}
            canWrite={canWrite}
            onOpen={open}
            onMove={(lead, status) => {
              if (status === "lost") return setLosing(lead);
              if (status === "converted" || status === "pending_move_in") {
                return void navigate({
                  to: "/crm/leads/$leadId",
                  params: { leadId: lead.id },
                  search: { convert: status === "converted" ? "move-in" : "pending" },
                });
              }
              setStatus.mutate({ leadId: lead.id, status: status as Status });
            }}
          />
        )
      ) : (
        <TableCard>
          {leads.isPending ? (
            <div className="flex flex-col gap-3 p-4">
              {[0, 1, 2, 3, 4].map((index) => (
                <Skeleton key={`row-${index}`} className="h-14 w-full" />
              ))}
            </div>
          ) : leads.isError ? (
            <ReadFailed
              className="p-4"
              message="Could not load leads. None are shown because none were read, not because none exist."
              error={leads.error}
              retrying={leads.isFetching}
              onRetry={() => void leads.refetch()}
            />
          ) : rows.length === 0 ? (
            <LeadsEmpty
              filters={filters}
              onClear={() => changeFilters({ ...EMPTY_FILTERS, scope: filters.scope })}
              onShowAll={() => changeFilters({ ...filters, scope: "all" })}
              onAdd={() => setEditing("new")}
            />
          ) : (
            <LeadsTable
              rows={rows}
              total={total}
              page={page}
              pageSize={pageSize}
              facilityId={facilityId}
              sort={sort}
              onSort={(key, direction) =>
                patch({ sort: key || undefined, dir: direction === "desc" ? "desc" : undefined })
              }
              onPage={(next) => patch({ page: next > 1 ? next : undefined })}
              onPageSize={(next) =>
                patch({ size: next === 25 ? undefined : next, page: undefined })
              }
              canWrite={canWrite}
              onOpen={open}
              onEdit={(lead) => setEditing({ id: lead.id })}
              onDelete={setDeleting}
            />
          )}
        </TableCard>
      )}

      {editing === "new" ? (
        <LeadFormDialog
          lead={null}
          defaultFacilityId={facilityId === "all" ? "" : facilityId}
          onClose={closeEditor}
          onCreated={setWelcoming}
        />
      ) : editing && editingLead.data ? (
        <LeadFormDialog
          lead={editingLead.data}
          defaultFacilityId={editingLead.data.facilityId}
          onClose={closeEditor}
        />
      ) : null}
      {welcoming ? (
        <LeadWelcomeOverlay lead={welcoming} onClose={() => setWelcoming(null)} />
      ) : null}
      {deleting ? (
        <RemoveConfirm
          title={`Delete ${deleting.firstName} ${deleting.lastName}?`}
          description="The lead, its tours, notes and history are removed for good."
          confirmLabel="Delete lead"
          onConfirm={() => remove.mutate({ leadId: deleting.id })}
          onClose={() => setDeleting(null)}
        />
      ) : null}
      {merging ? (
        <LeadMergeDialog leads={mergeable.data?.rows ?? []} onClose={() => setMerging(false)} />
      ) : null}
      {losing ? (
        <LeadLostDialog
          facilityId={losing.facilityId}
          saving={setStatus.isPending}
          onCancel={() => setLosing(null)}
          onConfirm={({ reasonId, note }) =>
            setStatus.mutate({
              leadId: losing.id,
              status: "lost",
              reasonId,
              lostReason: note || null,
            })
          }
        />
      ) : null}
    </div>
  );
}
